// src/hooks/useJobPoller.ts
// Generic hook for polling async jobs (HeyGen / ElevenLabs).
// Polls at `interval` ms until status is completed or failed.

import { useCallback, useEffect, useRef, useState } from 'react';

export type PollingStatus = 'idle' | 'polling' | 'completed' | 'failed';

interface UseJobPollerOpts<T> {
  /** The function that checks job status. Must return an object with at least `status`. */
  checkFn: (jobId: string) => Promise<T>;
  /** The job ID to poll. Set to null to not poll. */
  jobId: string | null;
  /** Polling interval in ms. Default 3000. */
  interval?: number;
  /** Callback when job completes. */
  onComplete?: (result: T) => void;
  /** Callback when job fails. */
  onFail?: (result: T) => void;
}

interface UseJobPollerResult<T> {
  status: PollingStatus;
  data: T | null;
  error: string | null;
  cancel: () => void;
}

export function useJobPoller<T extends { status: string; error_message?: string | null }>({
  checkFn,
  jobId,
  interval = 3000,
  onComplete,
  onFail,
}: UseJobPollerOpts<T>): UseJobPollerResult<T> {
  const [status, setStatus] = useState<PollingStatus>('idle');
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setStatus('idle');
  }, []);

  useEffect(() => {
    if (!jobId) {
      setStatus('idle');
      setData(null);
      setError(null);
      return;
    }

    cancelledRef.current = false;
    setStatus('polling');
    setError(null);

    const poll = async () => {
      if (cancelledRef.current) return;
      try {
        const result = await checkFn(jobId);
        if (cancelledRef.current) return;
        setData(result);

        if (result.status === 'completed') {
          cancel();
          setStatus('completed');
          onComplete?.(result);
        } else if (result.status === 'failed') {
          cancel();
          setStatus('failed');
          setError(result.error_message || 'Job failed');
          onFail?.(result);
        }
      } catch (e: any) {
        if (cancelledRef.current) return;
        setError(e.message || 'Polling error');
      }
    };

    // Initial check
    poll();
    timerRef.current = setInterval(poll, interval);

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [jobId, interval]); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, data, error, cancel };
}
